<script lang="ts">
  import { page } from '$app/stores';

  import { get } from 'svelte/store';

  import { InstitutionType } from '@do/models';

  import { Icon } from '@shared/ui/icon';
  import { overlayActivity } from '@shared/ui/overlay/model';
  import { Portal } from '@shared/ui/portal';
  import { clickOutside, cn, debounce } from '@shared/utils';

  import { SearchApi, searchLibs, type SearchData } from '@entities/search';

  import { SearchForm } from '@features/search/search-form';

  import { getEntityUrl } from '../libs';
  import List from './list.svelte';
  import MetaBlock from './meta-block.svelte';

  const searchApi = new SearchApi(fetch);

  let value = '';

  let isOpen = false;
  let isLoading = false;
  let artworks: SearchData['artworks'] | null = null;
  let artists: SearchData['artists'] | null = null;
  let selections: SearchData['selections'] | null = null;
  let input: HTMLInputElement;

  $: trimmedValue = value.trim();
  $: if (isOpen) input?.focus();

  function handleOpen() {
    isOpen = true;
    overlayActivity.open();
  }

  function handleClose() {
    isOpen = false;
    overlayActivity.close();
  }

  function handleReset() {
    value = '';
    artworks = null;
    artists = null;
    selections = null;
  }

  const handleKeys = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();

      return handleClose();
    }
  };

  async function handleSearch(search: string) {
    const { data } = get(page);

    if (!search) {
      artworks = null;
      artists = null;
      selections = null;

      return;
    }

    try {
      const res = await searchApi.search({
        institutions: data.configurations.id,
        limit: 3,
        offset: 0,
        search: search,
      });

      const map = searchLibs.mapSearchToUI(res);

      artworks = map.artworks;

      if (data.configurations.type !== InstitutionType.Artist) {
        artists = map.artists;
      }

      selections = map.selections;
    } catch (error) {
      console.error(error);
    } finally {
      isLoading = false;
    }
  }

  const debouncedSearch = debounce((value: string) => handleSearch(value), 300);

  function triggerSearch({ detail }: { detail: string }) {
    const search = detail.trim();

    if (search) {
      isLoading = true;
    }

    debouncedSearch(search);
  }
</script>

<button on:click="{handleOpen}" class="h-6">
  <Icon name="magnifying-glass" />
</button>

<Portal>
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div
    class="{cn(
      'fixed left-0 top-2 z-40 mx-auto w-full -translate-y-[120%] px-5 transition-transform md:px-8 xl:px-12',
      {
        'translate-y-0': isOpen,
      },
    )}"
    on:clickOutsideAction="{handleClose}"
    use:clickOutside="{!isOpen}"
    on:keydown="{handleKeys}"
    on:wheel|preventDefault|nonpassive
    role="search"
  >
    <section
      class="bg-default-50/80 dark:bg-default-700/60 dark:text-default-50 mb-0.5 flex items-center gap-3 rounded-lg p-3 backdrop-blur-2xl md:px-4"
    >
      <Icon name="magnifying-glass" classes="shrink-0" />

      <SearchForm
        bind:isLoading
        bind:value
        bind:input
        on:close="{handleClose}"
        on:input="{triggerSearch}"
        on:reset="{handleReset}"
      />
    </section>

    {#if trimmedValue.length > 1 && !isLoading}
      {@const hasList = artists?.data.length || artworks?.data.length}

      <section
        class="{cn(
          'bg-default-50/80 dark:bg-default-700/60 dark:text-default-50 flex max-h-[75vh] items-start gap-3 overflow-auto rounded-lg p-3 backdrop-blur-2xl md:items-center md:px-4',
          {
            'rounded-b-none': hasList,
          },
        )}"
      >
        {#if hasList}
          <List {artworks} {artists} {selections} on:navigate="{handleClose}" search="{trimmedValue}" />
        {:else if trimmedValue}
          <p>No results found for “{trimmedValue}”</p>
        {/if}
      </section>

      {#if hasList}
        {@const baseUrl = `/search`}
        {@const pageType = getEntityUrl({
          artworks: artworks?.data.length || 0,
          artists: artists?.data.length || 0,
          selections: selections?.data.length || 0,
        })}
        {@const navigateUrl = `${baseUrl}/${pageType}/`}

        <MetaBlock search="{trimmedValue}" url="{navigateUrl}" on:navigate="{handleClose}" />
      {/if}
    {/if}
  </div>
</Portal>
